import { Box, Typography } from "@mui/material";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";

import type { ToolCall, ToolCallCategory } from "../../contexts/chat/type";
import { CHAT_FONT_STACK } from "../../contexts/material_ui/theme";
import { toolCategoryLabel } from "./customer_facing";

interface ToolCallsContainerProps {
  toolCalls: ToolCall[];
  isStreaming: boolean;
}

export const ToolCallsContainer = ({
  toolCalls,
  isStreaming,
}: ToolCallsContainerProps) => {
  if (toolCalls.length === 0) return null;

  const categories = Array.from(
    new Set(toolCalls.map((toolCall) => toolCall.category)),
  ) as ToolCallCategory[];
  const hasError = toolCalls.some((toolCall) => toolCall.status === "error");
  const isActive =
    isStreaming ||
    toolCalls.some(
      (toolCall) =>
        toolCall.status === "pending" || toolCall.status === "running",
    );
  const headline = isActive
    ? "Tracing contributing data"
    : hasError
      ? "Some data checks were unavailable"
      : "Data checks complete";
  const Icon = isActive
    ? AutoAwesomeOutlinedIcon
    : hasError
      ? ErrorOutlineRoundedIcon
      : CheckCircleOutlineRoundedIcon;
  const color = isActive ? "#2A5FDB" : hasError ? "#9A6700" : "#287A4B";

  return (
    <Box
      aria-label="Answer checks"
      sx={{
        mx: 1,
        my: 0.75,
        px: 1.4,
        py: 1.2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: hasError
          ? "rgba(154, 103, 0, 0.2)"
          : "rgba(56, 119, 247, 0.14)",
        bgcolor: hasError
          ? "rgba(255, 193, 7, 0.055)"
          : "rgba(56, 119, 247, 0.025)",
        fontFamily: CHAT_FONT_STACK,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Icon sx={{ fontSize: 17, color }} />
        <Typography
          sx={{
            fontFamily: CHAT_FONT_STACK,
            fontSize: "0.78rem",
            lineHeight: 1.35,
            fontWeight: 700,
            color: "text.primary",
          }}
        >
          {headline}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.6,
          mt: 0.8,
        }}
      >
        {categories.map((category) => (
          <Box
            key={category}
            component="span"
            sx={{
              px: 0.8,
              py: 0.35,
              borderRadius: 99,
              bgcolor: "rgba(16, 17, 19, 0.045)",
              color: "text.secondary",
              fontFamily: CHAT_FONT_STACK,
              fontSize: "0.7rem",
              lineHeight: 1.3,
            }}
          >
            {toolCategoryLabel(category)}
          </Box>
        ))}
      </Box>
    </Box>
  );
};
